export type RGB = [number, number, number];

const NAMED: Record<string, string> = { black: "#000000", white: "#ffffff" };

export function parse(c: string | undefined | null): RGB | null {
  if (!c) return null;
  let s = (NAMED[c.trim().toLowerCase()] ?? c).trim();
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(s);
  if (m) {
    s = m[1].length === 3 ? m[1].replace(/./g, (x) => x + x) : m[1];
    return [parseInt(s.slice(0, 2), 16), parseInt(s.slice(2, 4), 16), parseInt(s.slice(4, 6), 16)];
  }
  const r = /^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i.exec(s);
  return r ? [+r[1], +r[2], +r[3]] : null;
}

const h2 = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");
export const hex = (c: RGB) => `#${h2(c[0])}${h2(c[1])}${h2(c[2])}`;

export function mix(a: string, b: string, t: number): string {
  const A = parse(a) ?? [0, 0, 0], B = parse(b) ?? [255, 255, 255];
  return hex([A[0] * t + B[0] * (1 - t), A[1] * t + B[1] * (1 - t), A[2] * t + B[2] * (1 - t)]);
}

export function lum(c: string): number {
  const [r, g, b] = (parse(c) ?? [0, 0, 0]).map((v) => { const x = v / 255; return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4; });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export const contrast = (a: string, b: string) => { const x = lum(a), y = lum(b); return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05); };

export function darkenTo(c: string, maxLum: number): string {
  let out = c;
  for (let i = 0; i < 12 && lum(out) > maxLum; i++) out = mix(out, "#000000", 0.88);
  return out;
}

export function lightenTo(c: string, minLum: number): string {
  let out = c;
  for (let i = 0; i < 14 && lum(out) < minLum; i++) out = mix(out, "#ffffff", 0.86);
  return out;
}

export const normalize = (c: string | undefined | null, fallback: string) => { const p = parse(c); return p ? hex(p) : fallback; };
